import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isNil, isEmpty } from 'lodash';
import { createStructuredSelector } from 'reselect';
import { Container, Button, Icon, Divider, F, Span } from 'components/Ui';
import PromoBanner, { CHANNELS } from 'components/Promos/Banner';
import Location from 'containers/PrimoAccount/Locations/Location';
import LocationsDrawer from 'containers/PrimoAccount/Locations/Drawer';
import {
  selectBillingSummary,
  selectCurrentPaymentMethod,
  selectUserInfo,
  selectCreditCards,
  selectBankAccounts,
} from 'containers/PrimoProfile/selectors';
import {
  selectSelectedServiceLocation,
  selectHasMultiServiceLocations,
} from 'containers/PrimoAccount/selectors';
import {
  getBillingSummary,
  fetchPaymentMethods,
} from 'containers/PrimoProfile/actions';
import { autopayIcon } from 'images';
import { formatCurrency } from 'utils/common';
import commonStyles from 'containers/PrimoProfile/styles.scss';
import { GTM_TRACKING_TAGS } from 'utils/constants';
import MigrationAlert from './MigrationAlert';
import styles from './styles.scss';

const AccountHeader = ({
  userInfo: { firstName, nickname },
  billingSummary,
  defaultPaymentMethod,
  openPayBillDrawer,
  openAutopayDrawer,
  serviceLocation,
  hasMultiShipTos,
  ...props
}) => {
  const dispatch = useDispatch();
  const [showLocsDrawer, setShowLocsDrawer] = useState(false);

  useEffect(() => {
    props.fetchPaymentMethods();
  }, []);

  const name = firstName ? `, ${firstName}` : nickname ? `, ${nickname}` : '';

  const { totalCurrentBalance } = billingSummary;

  return (
    <>
      <PromoBanner channel={CHANNELS.ss} />
      <div className={styles.accountHeader}>
        <Container value="Account Header" className={styles.container}>
          <div className={styles.userInfo}>
            <h2>
              <F>Hi</F>
              {name}!
            </h2>
            <div className={styles.controllers}>
              <Button
                disabled={!serviceLocation}
                text="My Profile"
                color="dark"
                icon={<Icon icon="user" />}
                iconPosition="left"
                onClick={() => dispatch(push(`/account/profile`))}
              />
            </div>
            <MigrationAlert onClick={() => setShowLocsDrawer(true)} />
          </div>
          <div className={styles.billAndAddress}>
            <div className={styles.payBill}>
              <div className={styles.currentBalance}>
                <Span className={styles.label}>Current balance:</Span>
                <div className={styles.amount}>
                  {formatCurrency(totalCurrentBalance, '')}
                </div>
              </div>
              <div className={styles.buttonContainer}>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div
                  className={classNames(
                    commonStyles.iconContainer,
                    styles.autopayContainer,
                  )}
                  data-tracking={
                    GTM_TRACKING_TAGS[
                      !isNil(defaultPaymentMethod) &&
                      defaultPaymentMethod.autopay
                        ? 'Autopay enabled'
                        : 'Enable Autopay'
                    ]
                  }
                  onClick={
                    !isNil(defaultPaymentMethod) && defaultPaymentMethod.autopay
                      ? () => {}
                      : openAutopayDrawer
                  }
                >
                  <img
                    className={commonStyles.icon}
                    src={autopayIcon}
                    alt="autopay"
                    aria-hidden="true"
                  />
                  <Span
                    className={classNames(commonStyles.smallText, styles.white)}
                  >
                    {!isNil(defaultPaymentMethod) &&
                    defaultPaymentMethod.autopay
                      ? 'Autopay enabled'
                      : 'Enable Autopay'}
                  </Span>
                </div>
                <Button
                  text="Pay My Bill"
                  onClick={openPayBillDrawer}
                  color="dark"
                  icon={<Icon icon="credit-card" className={styles.icon} />}
                  iconPosition="left"
                  className={styles.payMyBillButton}
                  fullWidth
                  disabled={isEmpty(billingSummary)}
                />
              </div>
            </div>
            <Divider className={styles.divider} />
            <dl className={styles.location}>
              <dt>
                <Span>Delivery address:</Span>
              </dt>
              <dd>
                <Location showId {...serviceLocation} />
              </dd>
            </dl>
            {hasMultiShipTos && (
              <>
                <Button
                  text="Select Address"
                  onClick={() => setShowLocsDrawer(true)}
                  color="dark"
                  className={styles.selectLocationBtn}
                />
                <LocationsDrawer
                  open={showLocsDrawer}
                  onClose={() => setShowLocsDrawer(false)}
                />
              </>
            )}
          </div>
        </Container>
      </div>
    </>
  );
};

AccountHeader.propTypes = {
  // getBillingSummary: PropTypes.func.isRequired,
  userInfo: PropTypes.object.isRequired,
  defaultPaymentMethod: PropTypes.object,
  billingSummary: PropTypes.object.isRequired,
  openPayBillDrawer: PropTypes.func,
  openAutopayDrawer: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  userInfo: selectUserInfo(),
  defaultPaymentMethod: selectCurrentPaymentMethod(),
  billingSummary: selectBillingSummary(),
  serviceLocation: selectSelectedServiceLocation(),
  hasMultiShipTos: selectHasMultiServiceLocations(),
  creditCards: selectCreditCards(),
  bankAccounts: selectBankAccounts(),
});

const mapDispatchToProps = {
  getBillingSummary,
  fetchPaymentMethods,
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountHeader);
